import type { MouseEvent, ReactElement, ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import CheckboxButton from 'LuxKit/Checkbox/CheckboxButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import { didClickInteractableElement } from 'components/Common/NestableClickable/NestableClickable'
import type { CartPanelItem } from '../hooks/useCartPanelItems'
import {
  cartPanelMobileHeaderImageStyles,
  cartPanelMobileTileBodyStyles,
  cartPanelMobileTileFooterStyles,
  cartPanelMobileTileHeaderStyles,
  cartPanelMobileTileRootStyles,
} from './cartPanelLayout.styles'

const MobileTileRoot = styled.div`
  ${cartPanelMobileTileRootStyles}
`

const TileHeader = styled.div`
  ${cartPanelMobileTileHeaderStyles}
`

const HeaderImage = styled.div`
  ${cartPanelMobileHeaderImageStyles}
`

const TileBody = styled.div`
  ${cartPanelMobileTileBodyStyles}
`

const TileFooter = styled.div`
  ${cartPanelMobileTileFooterStyles}
`

const PriceArea = styled.div`
  flex: 1;
  min-width: 0;
`

const DeleteArea = styled.div`
  flex-shrink: 0;
`

function stopPropagation(e: MouseEvent) {
  e.stopPropagation()
}

interface Props<TItem extends CartPanelItem> {
  item: TItem
  onToggleInclude: (item: TItem) => void
  onCardClick?: () => void
  layoutTitle: string
  layoutSubtitle?: string
  headerPrefix?: ReactElement
  mobileMetaData?: ReactElement
  topBanner?: ReactNode
  roomCard: ReactNode | undefined
  attention: ReactNode | undefined
  deleteAction: ReactElement
  price: ReactNode | undefined
  isToggleDisabled?: boolean
  image?: App.Image
}

function CartPanelAccommodationMobileTileLayout<TItem extends CartPanelItem>({
  item,
  onToggleInclude,
  onCardClick,
  layoutTitle,
  layoutSubtitle,
  headerPrefix,
  mobileMetaData,
  topBanner,
  roomCard,
  attention,
  deleteAction,
  price,
  isToggleDisabled = false,
  image,
}: Props<TItem>) {
  const handleCheckboxChange = useCallback(() => {
    onToggleInclude(item)
  }, [onToggleInclude, item])

  const handleTileClickCapture = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!onCardClick || !(event.target instanceof HTMLElement)) return
    if (didClickInteractableElement(event.target, event.currentTarget)) return
    onCardClick()
  }, [onCardClick])

  const isInteractive = !item.isBooked

  const includeCheckbox = useMemo(() => isInteractive ? (
    <div onClick={stopPropagation} data-testid="cart-panel-accommodation-mobile-checkbox">
      <CheckboxButton
        checked={!item.cartExcluded}
        onChange={handleCheckboxChange}
        disabled={isToggleDisabled}
        size="medium"
      />
    </div>
  ) : undefined, [handleCheckboxChange, isInteractive, isToggleDisabled, item.cartExcluded])

  return (
    <MobileTileRoot
      className={clsx({
        clickable: !!onCardClick,
        deselected: item.cartExcluded,
      })}
      onClickCapture={onCardClick ? handleTileClickCapture : undefined}
    >
      {topBanner && <div onClick={stopPropagation}>{topBanner}</div>}
      <TileHeader
        className={clsx({
          'has-checkbox': !!includeCheckbox,
          'with-image': !!image,
        })}
      >
        {includeCheckbox}
        <Group direction="vertical" gap={4}>
          {headerPrefix}
          <Heading variant="heading6">{layoutTitle}</Heading>
          {layoutSubtitle && (
            <BodyText variant="medium" colour="light-foreground-alt">{layoutSubtitle}</BodyText>
          )}
          {mobileMetaData}
        </Group>
        {image && (
          <HeaderImage>
            <CartReviewTileImageContent image={image} objectFit="cover" />
          </HeaderImage>
        )}
      </TileHeader>
      <TileBody data-testid="cart-panel-accommodation-mobile-body">
        {roomCard && (
          <div onClick={stopPropagation} data-testid="cart-panel-accommodation-mobile-extension">
            {roomCard}
          </div>
        )}
        {attention && <div onClick={stopPropagation}>{attention}</div>}
        <TileFooter data-testid="cart-panel-accommodation-mobile-footer">
          <PriceArea>{price}</PriceArea>
          <DeleteArea onClick={stopPropagation}>{deleteAction}</DeleteArea>
        </TileFooter>
      </TileBody>
    </MobileTileRoot>
  )
}

export default CartPanelAccommodationMobileTileLayout
